import { useMemo } from 'react';
import { sparklineFractions } from '../../../lib/site-analytics';
import styles from './Sparkline.module.css';

// A trend line with no axes (spec 2026-10-25 §4, the dataviz stat-tile contract): one 2px stroke in the accent, a ~10%
// wash under it, and the last day as a dot ringed in the surface colour. Baseline 0, so the shape never exaggerates; an
// all-zero series is a flat line along the bottom (drawn, not hidden). Geometry is derived once per series + size.
// Decorative: the tile that holds it states the number and the trend in words.

const PAD = 6; // room for the end dot and its ring

export function sparklinePath(values: readonly number[], width: number, height: number) {
  const fr = sparklineFractions(values);
  if (fr.length === 0) return null;
  const innerW = width - PAD * 2;
  const innerH = height - PAD * 2;
  const step = fr.length > 1 ? innerW / (fr.length - 1) : 0;
  const pts = fr.map((f, i) => [PAD + (fr.length > 1 ? i * step : innerW), PAD + innerH * (1 - f)] as const);
  const line = pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`).join(' ');
  const base = (PAD + innerH).toFixed(2);
  const area = `${line} L${pts[pts.length - 1][0].toFixed(2)},${base} L${pts[0][0].toFixed(2)},${base} Z`;
  const [ex, ey] = pts[pts.length - 1];
  return { line: fr.length > 1 ? line : `M${PAD},${ey.toFixed(2)} L${ex.toFixed(2)},${ey.toFixed(2)}`, area, end: { x: ex, y: ey } };
}

export function Sparkline({ values, width = 120, height = 36, className }: { values: readonly number[]; width?: number; height?: number; className?: string }) {
  const geo = useMemo(() => sparklinePath(values, width, height), [values, width, height]);
  if (!geo) return null;
  return (
    <svg className={`${styles.spark}${className ? ` ${className}` : ''}`} width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden focusable="false">
      <path d={geo.area} className={styles.area} />
      <path d={geo.line} className={styles.line} />
      <circle cx={geo.end.x} cy={geo.end.y} r={4} className={styles.dot} />
    </svg>
  );
}
